import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { List, ListItem, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import QuoteForm from '../../components/QuoteForm';
import { ENV, STRINGS, ANALYTICS } from '../../constants';
import ConfigSlice from '../../redux/configSlice';
import useBreakpoint from '../../hooks/useBreakpoint';
import useAnalytics from '../../hooks/useAnalytics';
import { formatMessage } from '../../utils/strings';
import { addTrailingZeros } from '../../helpers/normalizers';
import { FAQ, PICKUP_FINDER } from '../../constants/routes';

const homeImg1 = `${ENV.CDN_BASE_URL_IMAGES}/homeImg1.png`;
const homeImg2 = `${ENV.CDN_BASE_URL_IMAGES}/homeImg2.png`;
const homeImg3 = `${ENV.CDN_BASE_URL_IMAGES}/homeImg3.png`;
const bgMain = `${ENV.CDN_BASE_URL_IMAGES}/homeBackgroundMain.png`;

function Home() {
  const { setInterfaceId, Trackable } = useAnalytics();
  const { minDomestic, minInternational } = useSelector(
    ConfigSlice.selectors.getConfigPrices
  );
  const isMediumScreen = useBreakpoint('lg');
  const isSmallScreen = useBreakpoint('md');

  useEffect(() => {
    setInterfaceId(ANALYTICS.HOME.INTERFACE_ID);
  }, []);

  const sendParcelSection = (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        position: { md: 'relative', lg: 'absolute' },
        bottom: 0,
        width: '100%',
        display: 'flex',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'primary.main',
          display: 'flex',
          justifyContent: 'space-between',
          paddingX: 4,
          paddingY: 4,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Box>
          <Typography
            variant='h1'
            sx={{
              color: 'primary.contrastText',
              textAlign: { sm: 'left', md: 'center' },
            }}
          >
            {STRINGS.SEND_PARCEL_HOME_SECTION.TITLE}
          </Typography>
        </Box>
        <Box
          sx={{
            paddingTop: 3,
            flexGrow: 1,
          }}
        >
          <QuoteForm />
        </Box>
      </Box>
    </Box>
  );

  const sectionContainer = ({
    img,
    imgPozLeft,
    descriptionContent,
    bgColor,
  }) => (
    <Box
      sx={{
        display: 'flex',
        gap: '12px',
        paddingBottom: '12px',
        flexDirection: {
          xs: 'column-reverse',
          lg: imgPozLeft ? 'row' : 'row-reverse',
        },
      }}
    >
      <Box
        sx={{
          display: { xs: 'flex', md: 'block' },
          justifyContent: { xs: 'center', md: 'flex-end' },
          width: '100%',
          backgroundColor: bgColor,
        }}
      >
        <img
          src={img}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          alt={'img'}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: '100%',
          backgroundColor: !imgPozLeft ? 'primary.main' : undefined,
        }}
      >
        <Box sx={{ px: { xs: 4, lg: 5 }, py: { xs: 5, lg: 0 } }}>
          {descriptionContent}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Trackable loadId={ANALYTICS.HOME.LOAD}>
      <Box sx={{ flexGrow: 1 }}>
        {!isSmallScreen && (
          <Box
            sx={{
              marginBottom: '12px',
              backgroundImage: `url(${bgMain})`,
              backgroundSize: { xs: 'cover', md: 'cover' },
              backgroundRepeat: 'no-repeat',
              width: '100%',
              height: 0,
              paddingTop: {
                xs: 'calc(225 / 390 * 100%)',
                sm: '700px',
                md: 'calc(500 / 1200 * 100%)',
              },
              position: 'relative',
              backgroundPosition: {
                sm: '50%',
                md: 'center calc(500 / 1200 * 50%)',
              },
            }}
          >
            {!isMediumScreen && sendParcelSection}
          </Box>
        )}
        {isMediumScreen && sendParcelSection}
        {sectionContainer({
          img: homeImg1,
          imgPozLeft: true,
          bgColor: 'primary.main',
          descriptionContent: (
            <>
              <Typography variant='h2'>
                {STRINGS.SEND_PARCEL_HOME_SECTION_DESCRIPTION.TITLE}
              </Typography>
              <Box sx={{ width: { sx: '100%' }, mr: 2 }}>
                <Typography component='h3' variant='subtitle1'>
                  {formatMessage(
                    STRINGS.SEND_PARCEL_HOME_SECTION_DESCRIPTION.SUBTITLE,
                    STRINGS.CURRENCY_SYMBOLS.GBP,
                    addTrailingZeros(minDomestic)
                  )}
                </Typography>
                <List>
                  {STRINGS.SEND_PARCEL_HOME_SECTION_DESCRIPTION.OPTIONS.map(
                    option => (
                      <ListItem
                        key={option}
                        sx={{
                          display: 'list-item',
                          listStyleType: 'disc',
                          ml: 4,
                        }}
                      >
                        <Typography variant={'body1'}>
                          {formatMessage(
                            option,
                            STRINGS.CURRENCY_SYMBOLS.GBP,
                            addTrailingZeros(minInternational)
                          )}
                        </Typography>
                      </ListItem>
                    )
                  )}
                </List>
              </Box>
            </>
          ),
        })}
        {sectionContainer({
          img: homeImg2,
          imgPozLeft: false,
          descriptionContent: (
            <>
              <Typography
                variant='h2'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.SEND_YOUR_PARCEL_ANYWHERE}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {formatMessage(
                  STRINGS.DPD_CAN_SHIP,
                  STRINGS.CURRENCY_SYMBOLS.GBP,
                  addTrailingZeros(minInternational)
                )}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText' }}
              >
                {STRINGS.DPD_CAN_SHIP_OPTIONS}
              </Typography>
            </>
          ),
        })}
        {sectionContainer({
          img: homeImg3,
          imgPozLeft: true,
          bgColor: 'primary.main',
          descriptionContent: (
            <>
              <Typography variant='h2' sx={{ py: 2 }}>
                {STRINGS.LOCAL_DROP_OFF_AND_COLLECT}
              </Typography>
              <Typography variant='body1'>
                {STRINGS.LOCAL_DROP_OFF_AND_COLLECT_DESCRIPTION}
              </Typography>
            </>
          ),
        })}
        {sectionContainer({
          img: homeImg1,
          imgPozLeft: false,
          descriptionContent: (
            <>
              <Typography
                variant='h2'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.CONTACT_DPD_ONLINE}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.CONTACT_DPD_ONLINE_DESCRIPTION}
              </Typography>
            </>
          ),
        })}
        {sectionContainer({
          img: homeImg2,
          imgPozLeft: true,
          bgColor: 'primary.main',
          descriptionContent: (
            <>
              <Typography variant='h2' sx={{ py: 2 }}>
                {STRINGS.FIND_YOUR_NEAREST_PICKUP_POINT}
              </Typography>
              <Typography variant='body1'>
                {STRINGS.FIND_YOUR_NEAREST_PICKUP_POINT_DESCRIPTION}
                <Link href={PICKUP_FINDER} target={'_blank'} rel='noreferrer'>
                  {STRINGS.HERE}
                </Link>
              </Typography>
            </>
          ),
        })}
        {/* NOTE: Removed until bulk upload functionality will be introduced */}
        {/* {sectionContainer({
          img: homeImg3,
          imgPozLeft: false,
          descriptionContent: (
            <>
              <Typography
                variant='h2'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.SHIP_IN_BULK}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.SHIP_IN_BULK_DESCRIPTION}
              </Typography>
            </>
          ),
        })} */}
        {sectionContainer({
          img: homeImg1,
          imgPozLeft: false,
          descriptionContent: (
            <Box py={4}>
              <Typography
                variant='h2'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.WHAT_IS_DPD_ONLINE}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.YOUR_RELIABLE_PARCEL}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.DPD_ONLINE_IS_YOUR}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.WITH_OVER_DROP_OFF}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.YOU_HAVE_ULTIMATE}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.PLUS_YOU_HAVE}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'primary.contrastText', mb: { xs: 1, md: 2 } }}
              >
                {STRINGS.FOR_MORE_INFORMATION}
                <Link
                  href={FAQ}
                  target={'_blank'}
                  rel='noreferrer'
                  sx={{
                    color: 'primary.contrastText',
                    textDecoration: 'underline',
                  }}
                >
                  {STRINGS.HELP_CENTRE}
                </Link>
              </Typography>
            </Box>
          ),
        })}
      </Box>
    </Trackable>
  );
}

export default Home;
